import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@repletoryreach/sdk-auth';
import { identity, isApiError } from '@repletoryreach/sdk-api';
import { Stack, Card, Title, Button, Text, Group, Badge } from '@repletoryreach/sdk-ui';
import { PasswordInput } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { PasswordRules } from '../../../../app/components/PasswordRules';
import { isPasswordValid, passwordRefusalMessage } from '../../../../lib/password';
import { useT } from '../../../../i18n/I18nProvider';

export const SIGN_IN_METHODS_KEY = ['auth', 'sign-in-methods'];

const providerName = (p: string) => p.charAt(0).toUpperCase() + p.slice(1);

/**
 * How this person can get into their account (9 October 2026): their password, and Google, Facebook or Apple when
 * linked, each removable unless it is the last way in. Linking needs no button: signing in with Google using the
 * same confirmed email address links it, so the card says that rather than starting a flow that could open a second
 * account under another address.
 */
export function SignInMethodsCard() {
  const t = useT();
  const qc = useQueryClient();
  const { user } = useAuth();
  const methods = useQuery({ queryKey: SIGN_IN_METHODS_KEY, queryFn: () => identity.getSignInMethods() });
  const offered = useQuery({ queryKey: ['auth', 'providers'], queryFn: () => identity.listAuthProviders(), staleTime: 300_000 });
  const m = methods.data?.data;
  const linked = Array.isArray(m?.providers) ? m.providers : [];
  const unlinked = (Array.isArray(offered.data?.data) ? offered.data.data : []).filter((p) => p.enabled && !linked.includes(p.provider));

  const unlink = useMutation({
    mutationFn: (provider: string) => identity.unlinkAuthProvider(provider),
    onSuccess: (_, provider) => {
      void qc.invalidateQueries({ queryKey: SIGN_IN_METHODS_KEY });
      notifySuccess(t('settings.security.methods.removed', { provider: providerName(provider) }), t('settings.security.methods.title'));
    },
    onError: (e) => notifyError(
      isApiError(e) && e.code === 'LAST_SIGN_IN_METHOD' ? t('settings.security.methods.lastMethod') : (e instanceof Error ? e.message : t('common.unknownError')),
      t('settings.security.methods.title'))
  });

  function confirmRemove(provider: string) {
    openConfirmModal({
      title: t('settings.security.methods.removeTitle', { provider: providerName(provider) }),
      children: <Text role="bodySmall">{t('settings.security.methods.removeBody', { provider: providerName(provider) })}</Text>,
      labels: { confirm: t('settings.security.methods.remove'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => unlink.mutate(provider)
    });
  }

  return (
    <Card withBorder data-guide="security-methods" data-testid="sign-in-methods">
      <Stack gap="sm">
        <Title order={4}>{t('settings.security.methods.title')}</Title>
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.security.methods.description')}</Text>
        {methods.error && <Alert color="error">{methods.error instanceof Error ? methods.error.message : t('common.unknownError')}</Alert>}
        {m && (
          <Stack gap="xs">
            <Group justify="space-between" wrap="nowrap">
              <Text role="bodySmall" fw={600}>{t('settings.security.methods.password')}</Text>
              <Badge size="sm" variant="light" color={m.has_password ? 'green' : 'gray'}>
                {m.has_password ? t('settings.security.methods.passwordSet') : t('settings.security.methods.passwordNotSet')}
              </Badge>
            </Group>
            {linked.map((p) => (
              <Group key={p} justify="space-between" wrap="nowrap" data-testid={`method-${p}`}>
                <Stack gap={0}>
                  <Text role="bodySmall" fw={600}>{providerName(p)}</Text>
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.security.methods.linked')}</Text>
                </Stack>
                <Button size="xs" variant="subtle" color="red" loading={unlink.isPending && unlink.variables === p}
                  disabled={!m.has_password && linked.length <= 1} onClick={() => confirmRemove(p)}>
                  {t('settings.security.methods.remove')}
                </Button>
              </Group>
            ))}
            {!m.has_password && linked.length <= 1 && (
              <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.security.methods.lastMethod')}</Text>
            )}
            {unlinked.map((p) => (
              <Text key={p.provider} role="caption" c="var(--rr-color-text-muted)">
                {t('settings.security.methods.howToAdd', { provider: p.display_name, email: user?.email ?? '' })}
              </Text>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

/**
 * A first password, for someone who signed up with Google and has none (9 October 2026). Without one, Google is the
 * only way in, and losing that Google account would mean losing this one.
 */
export function SetPasswordCard() {
  const t = useT();
  const qc = useQueryClient();
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const valid = isPasswordValid(next);
  const save = useMutation({
    mutationFn: () => identity.setPassword(next),
    onSuccess: () => {
      setNext(''); setConfirm('');
      void qc.invalidateQueries({ queryKey: SIGN_IN_METHODS_KEY });
      notifySuccess(t('settings.security.setPassword.done'), t('settings.nav.security'));
    },
    onError: (e) => setError(passwordRefusalMessage(e, t) ?? (e instanceof Error ? e.message : t('settings.security.changeFailed')))
  });

  function submit() {
    setError(null);
    if (!valid) { setError(t('auth.reset.errors.weak')); return; }
    if (next !== confirm) { setError(t('auth.reset.errors.mismatch')); return; }
    save.mutate();
  }

  return (
    <Card withBorder data-guide="security-password" data-testid="set-password">
      <Stack gap="md">
        <Title order={4}>{t('settings.security.setPassword.title')}</Title>
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.security.setPassword.description')}</Text>
        <PasswordInput label={t('auth.reset.form.newPassword')} value={next} onChange={(e) => setNext(e.currentTarget.value)} autoComplete="new-password" />
        {next.length > 0 && <PasswordRules value={next} />}
        <PasswordInput label={t('settings.security.confirmNewPassword')} value={confirm} onChange={(e) => setConfirm(e.currentTarget.value)} autoComplete="new-password" />
        {error && <Alert color="error">{error}</Alert>}
        <div>
          <Button onClick={submit} loading={save.isPending} disabled={!valid || !confirm}>{t('settings.security.setPassword.save')}</Button>
        </div>
      </Stack>
    </Card>
  );
}
